import React, { ReactElement, useCallback, useMemo } from "react";
import {
    ActionContextType,
    FormField,
    Workflow,
    useGetExternalFormFieldsQuery, useGetFormDataQuery,
    useGetFormQuery,
} from "../../../../__generated__/api";
import { removeTypename } from "../../../../api";
import { Loading } from "../../../../components";
import useArticleContext from "../../../../contexts/HelpContext";
import useRouting from "../../../../contexts/Routing";
import { ErrorRemote } from "../../../error/Error";
import AutomationFlow from "../../../../components/workflow-v3/AutomationFlow";
import { ACTION_TYPE_MAP } from "../../../../components/workflow-v3/workflow/node-mappings";

export default function ActionFormV3({ id }: { id: string }): ReactElement {
    useArticleContext("urn:fq:article:7c8f62a03cbb41bc8a73612c329879f4");

    const { Paths, historyPush } = useRouting();

    const isNew = id === "new";

    const {
        data, error, loading: isLoading,
    } = useGetFormQuery({
        fetchPolicy: "cache-and-network",
        skip: isNew,
        variables: {
            id: isNew ? undefined : id,
            hasId: true,
        },
        notifyOnNetworkStatusChange: true,
    });

    const externalFormId = useMemo(() => data?.form?.externalFormId, [data?.form?.externalFormId]);

    const { data: formData, loading: isDataLoading } = useGetFormDataQuery({
        variables: {
            shouldIncludeTemplateFields: false,
        },
    });
    const { data: externalData } = useGetExternalFormFieldsQuery({
        variables: {
            id: externalFormId,
        },
        skip: externalFormId == null,
    });

    const fields = useMemo(() => (structuredClone(data?.form?.fields) ?? [])
        .map(field => removeTypename(field)) ?? [], [data?.form?.fields]);

    const handleHideAutomation = useCallback(() => {
        historyPush(Paths.ADMINISTRATOR_SETTING, {
            setting: "workflow-actions",
        });
    }, []);

    const fieldMap = useMemo(() => formData?.formFields.toIdMap("id") ?? {}, [formData]);
    const formFields = useMemo(() => (externalData?.externalFormFields as FormField[]
        ?? fields.mapFilter(row => (row.fieldId ? fieldMap[row.fieldId] : undefined))), [externalData, fields, fieldMap]);

    const actionsContextType = useMemo(() => ACTION_TYPE_MAP[data?.form?.workflow?.steps?.firstElement?.actionId]
    ?? ActionContextType.GenericNoTicket, [data?.form?.workflow?.steps?.firstElement?.actionId]);

    if (!data?.form || isLoading || isDataLoading) {
        return <Loading />;
    }
    if (error) {
        return <ErrorRemote error={error} />;
    }

    return (
        <AutomationFlow
            workflow={data.form.workflow as Workflow}
            onHide={handleHideAutomation}
            formFields={formFields}
            actionsContextType={actionsContextType}
        />
    );
}
